import React, { createContext, useContext, useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import axios from 'axios';
import config from 'config';

const ProfileContext = createContext();

export const useProfile = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};

export const ProfileProvider = ({ children }) => {
  const [profiles, setProfiles] = useState([]);
  const [currentProfile, setCurrentProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [canWrite, setCanWrite] = useState(true); // Default to true for licence owners
  const [canReadAiReport, setCanReadAiReport] = useState(true); // Default to true for licence owners

  // Load profiles on mount
  useEffect(() => {
    loadProfiles();
  }, []);

  const persistActiveProfileId = async (profilId) => {
    if (!Cookies.get('loggedIn') || !profilId) {
      return;
    }

    try {
      await axios.post(
        `${config.apiUrl}/profiles/active`,
        { profilId }
      );
    } catch (error) {
      console.error('Error saving active profile:', error);
    }
  };

  const loadProfiles = async () => {
    try {
      // Skip on public pages (auth layout) or if no token
      if (!Cookies.get('loggedIn') || window.location.hash.startsWith('#/auth')) {
        setIsLoading(false);
        return;
      }

      const response = await axios.get(`${config.apiUrl}/profiles`);

      const { profiles: loadedProfiles, activeProfilId } = response.data;
      setProfiles(loadedProfiles || []);

      // Use the active profile stored in DB first, fallback to cookie, then first profile
      const savedProfileId = activeProfilId || Cookies.get('currentProfileId');
      const savedProfile = loadedProfiles?.find(p => String(p.id) === String(savedProfileId));
      
      if (savedProfile) {
        setCurrentProfile(savedProfile);
        setCanWrite(savedProfile.can_write !== false);
        setCanReadAiReport(savedProfile.can_read_ai_report !== false);
        Cookies.set('currentProfileId', savedProfile.id, { expires: 365 });
      } else if (loadedProfiles?.length > 0) {
        const fallbackProfile = loadedProfiles[0];
        setCurrentProfile(fallbackProfile);
        setCanWrite(fallbackProfile.can_write !== false);
        setCanReadAiReport(fallbackProfile.can_read_ai_report !== false);
        Cookies.set('currentProfileId', fallbackProfile.id, { expires: 365 });
        await persistActiveProfileId(fallbackProfile.id);
      }
    } catch (error) {
      console.error('Error loading profiles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const switchProfile = async (profile) => {
    setCurrentProfile(profile);
    setCanWrite(profile.can_write !== false);
    setCanReadAiReport(profile.can_read_ai_report !== false);
    Cookies.set('currentProfileId', profile.id, { expires: 365 });
    await persistActiveProfileId(profile.id);
    // Reload the page to refresh all data with new profile
    window.location.reload();
  };

  const refreshProfiles = async () => {
    await loadProfiles();
  };

  const value = {
    profiles,
    currentProfile,
    isLoading,
    canWrite,
    canReadAiReport,
    switchProfile,
    refreshProfiles,
    setProfiles,
    setCurrentProfile
  };

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
};

export default ProfileContext;
